import React, { useCallback } from "react";
import { usePanel } from "hooks/usePanel";
import { Sizes } from "components/ui/Loader";
import { NsKey } from "enums/NsKey";
import { BorrowerInsuredForm } from "features/subscription-process/profiling-process/forms/borrower-insured";
import { ClipboardListIcon, FileTextIcon } from "lucide-react";
import { BorrowerNaturalPersonDto } from "api/borrower";
import { useMainBorrowerPricingActions, useMainBorrowerPricingState } from "../../contexts/main-borrower-pricing/MainBorrowerPricingContext";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { withPersonName } from "shared/utils/panelTitle";

interface BorrowerPricingInsuredCardProps {
  insured: BorrowerNaturalPersonDto
}

export const BorrowerPricingInsuredCard: React.FC<BorrowerPricingInsuredCardProps> = ({ insured }) => {
  const { openPanel } = usePanel()
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.Borrower])
  const { borrowerProjectId, borrowerId } = useMainBorrowerPricingState();
  const { initBorrowerPricingProcessAsync } = useMainBorrowerPricingActions();

  const reload = useCallback(() => {
    if (borrowerProjectId && borrowerId)
      initBorrowerPricingProcessAsync({ borrowerProjectId, emprunteurId: borrowerId })
  }, [borrowerProjectId, initBorrowerPricingProcessAsync, borrowerId])

  const onOpenSurvey = useCallback(() => {
    openPanel({
      title: withPersonName(t("COMMON.LABELS.EDIT_INSURED_PERSON"), `${insured.firstName} ${insured.lastName}`),
      size: Sizes["2xl"],
    },
      <BorrowerInsuredForm reload={reload} personId={insured.id} initialTab={2} borrowerProjectId={borrowerProjectId ?? undefined} />
    )
  }, [openPanel, t, insured, reload, borrowerProjectId])

  return (
    <div className="flex min-w-80 rounded-xl h-full p-4 border gap-3">
      <div className="flex-none h-8 w-8 flex justify-center items-center bg-secondary rounded-full">
        <ClipboardListIcon className="w-3.5 h-3.5 text-white" />
      </div>
      <div className="flex flex-col w-full gap-2">
        <p className="text-sm font-light">{t("BORROWER.LABELS.RISK_PROFILE")}</p>
        <p className="text-sm">{t("BORROWER.LABELS.RISKY_ACTIVITY")}</p>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={onOpenSurvey}
            className="text-sm py-0 px-1.5 rounded-full transition-all duration-200 focus:outline-none text-primary border border-primary hover:opacity-70 cursor-pointer flex items-center gap-1"
          >
            <FileTextIcon className="w-3 h-3" />
            {t("BORROWER.LABELS.INSURED_SITUATION")}
          </button>
        </div>
      </div>
    </div>
  );
}
